import { useQueryClient } from '@tanstack/react-query'
import {
	AlertTriangle,
	Bell,
	CheckCheck,
	CircleAlert,
	Image,
	Info,
	PlayCircle,
} from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useMarkNoticeAsRead, useNotices } from '@/api/queries/notices-queries'
import {
	useMarkNotificationAsRead,
	useNotifications,
} from '@/api/queries/notifications-queries'
import { AccessDeniedState, PageHeader } from '@/components/commons'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/utils'
import type {
	NoticeListOutput,
	NoticeMediaKind,
	NoticePriority,
	NoticeType,
	NoticeWithReadStatus,
} from '@/types/notice'
import type { SystemNotification } from '@/types/notification'
import { isForbiddenApiError } from '@/utils/api-error'
import { formatDate } from '@/utils/formatDate'
import { getNoticeMediaKind } from '@/utils/notice-media'

/* ── Helpers ── */

const typeConfig: Record<
	NoticeType,
	{
		icon: typeof Info
		alertClass: string
		iconClass: string
		variant: 'default' | 'destructive'
	}
> = {
	info: {
		icon: Info,
		alertClass: 'border-kaefer-sapphire-blue/30 bg-kaefer-sapphire-blue/5',
		iconClass: 'text-kaefer-sapphire-blue',
		variant: 'default',
	},
	warning: {
		icon: AlertTriangle,
		alertClass: 'border-kaefer-honey-yellow/30 bg-kaefer-honey-yellow/5',
		iconClass: 'text-kaefer-honey-yellow',
		variant: 'default',
	},
	danger: {
		icon: CircleAlert,
		alertClass: '',
		iconClass: '',
		variant: 'destructive',
	},
}

const priorityLabels: Record<string, string> = {
	high: 'Alta',
	medium: 'Média',
	low: 'Baixa',
}

type FeedItem = {
	id: string
	title: string
	message: string
	createdAt: string
	isRead: boolean
	kind: 'notice' | 'notification'
	noticeType: NoticeType
	priority?: NoticePriority
	mediaKind?: NoticeMediaKind
}

/* ── Page ── */

export function NoticesPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const {
		data: noticesData,
		isLoading: isNoticesLoading,
		error: noticesError,
	} = useNotices({ isActive: true })
	const {
		data: notificationsData,
		isLoading: isNotificationsLoading,
		error: notificationsError,
	} = useNotifications()
	const { mutateAsync: markNoticeAsRead } = useMarkNoticeAsRead()
	const { mutateAsync: markNotificationAsRead } = useMarkNotificationAsRead()

	const notices = noticesData?.items ?? []
	const notifications = notificationsData?.items ?? []

	const feedItems = useMemo<FeedItem[]>(() => {
		const noticeItems = notices.map((notice: NoticeWithReadStatus) => ({
			id: notice.id,
			title: notice.title,
			message: notice.message,
			createdAt: notice.createdAt,
			isRead: notice.isRead,
			kind: 'notice' as const,
			noticeType: notice.type,
			priority: notice.priority,
			mediaKind: getNoticeMediaKind(notice.media),
		}))

		const notificationItems = notifications.map(
			(notification: SystemNotification) => ({
				id: notification.id,
				title: notification.title,
				message: notification.message,
				createdAt: notification.createdAt ?? '',
				isRead: Boolean(notification.readAt),
				kind: 'notification' as const,
				noticeType: notification.type ?? 'info',
			}),
		)

		return [...noticeItems, ...notificationItems].sort((left, right) => {
			const leftTime = Date.parse(left.createdAt) || 0
			const rightTime = Date.parse(right.createdAt) || 0
			return rightTime - leftTime
		})
	}, [notices, notifications])

	const unreadItems = feedItems.filter((item) => !item.isRead)
	const readItems = feedItems.filter((item) => item.isRead)
	const unreadCount = unreadItems.length
	const isForbidden =
		isForbiddenApiError(noticesError) || isForbiddenApiError(notificationsError)

	if (isForbidden) {
		return <AccessDeniedState />
	}

	const markNoticeOptimistically = (id: string) => {
		queryClient.setQueriesData<NoticeListOutput>(
			{ queryKey: ['notices'] },
			(current) => {
				if (!current) return current
				return {
					...current,
					items: current.items.map((item) =>
						item.id === id ? { ...item, isRead: true } : item,
					),
				}
			},
		)
	}

	const handleItemClick = async (item: FeedItem) => {
		if (item.isRead) return

		if (item.kind === 'notice') {
			markNoticeOptimistically(item.id)
			try {
				await markNoticeAsRead(item.id)
			} catch (_error) {
				queryClient.invalidateQueries({ queryKey: ['notices'] })
			}
			return
		}

		try {
			await markNotificationAsRead(item.id)
		} catch (_error) {
			queryClient.invalidateQueries({ queryKey: ['notifications'] })
		}
	}

	const markAllAsRead = () => {
		for (const item of unreadItems) {
			void handleItemClick(item)
		}
	}

	const FeedList = ({ items }: { items: FeedItem[] }) => {
		if (isNoticesLoading || isNotificationsLoading) {
			return (
				<div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
					<p className="text-sm">Carregando...</p>
				</div>
			)
		}
		if (items.length === 0) {
			return (
				<div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
					<Bell className="mb-3 size-10 opacity-40" />
					<p className="text-sm">{t('notices.combinedEmpty')}</p>
				</div>
			)
		}
		return (
			<>
				<div className="flex flex-col gap-3">
					{items.map((item) => {
						const config = typeConfig[item.noticeType]
						const Icon = config.icon
						return (
							<button
								key={item.id}
								type="button"
								onClick={() => {
									if (item.kind === 'notice') {
										navigate(
											ROUTES.DASHBOARD.NOTICE_DETAIL.replace(':id', item.id),
										)
										return
									}
									void handleItemClick(item)
								}}
								className="block w-full text-left"
							>
								<Alert
									variant={config.variant}
									className={cn(
										'relative cursor-pointer transition-all duration-200 hover:shadow-sm',
										config.variant === 'default' && config.alertClass,
										!item.isRead &&
											'ring-2 ring-kaefer-sapphire-blue/20 shadow-sm',
										item.isRead && 'opacity-70',
									)}
								>
									<Icon
										className={cn(
											'h-5 w-5',
											config.variant === 'default' && config.iconClass,
										)}
									/>
									<div className="flex min-w-0 flex-1 flex-col gap-2">
										<AlertTitle className="flex flex-wrap items-center gap-2 pr-2">
											<span
												className={cn(
													'font-semibold',
													!item.isRead && 'text-kaefer-dark-grey',
												)}
											>
												{item.title}
											</span>
											{item.mediaKind === 'video' && (
												<PlayCircle className="size-4 text-muted-foreground" />
											)}
											{item.mediaKind === 'image' && (
												<Image className="size-4 text-muted-foreground" />
											)}
											<span
												className={cn(
													'rounded-full px-2 py-0.5 text-[11px] font-semibold',
													item.kind === 'notice'
														? 'bg-kaefer-dark-grey/10 text-kaefer-dark-grey'
														: 'bg-kaefer-sapphire-blue/10 text-kaefer-sapphire-blue',
												)}
											>
												{item.kind === 'notice'
													? t('notices.source.notice')
													: t('notices.source.notification')}
											</span>
											{!item.isRead && (
												<span className="inline-block h-2 w-2 rounded-full bg-kaefer-sapphire-blue" />
											)}
										</AlertTitle>
										<AlertDescription className="mt-1">
											<p className="text-sm leading-relaxed">{item.message}</p>
											<div className="mt-3 flex flex-wrap items-center gap-3">
												<span className="text-muted-foreground text-xs">
													{formatDate(item.createdAt)}
												</span>
												{item.kind === 'notice' && item.priority && (
													<span
														className={cn(
															'rounded-full px-2 py-0.5 text-xs font-medium',
															item.priority === 'high' &&
																'bg-red-100 text-red-700',
															item.priority === 'medium' &&
																'bg-yellow-100 text-yellow-700',
															item.priority === 'low' &&
																'bg-green-100 text-green-700',
														)}
													>
														{t('notices.priority')}:{' '}
														{priorityLabels[item.priority]}
													</span>
												)}
												<span
													className={cn(
														'rounded-full px-2 py-0.5 text-xs font-medium',
														item.isRead
															? 'bg-kaefer-light-silver/70 text-kaefer-dark-grey'
															: 'bg-kaefer-sapphire-blue/10 text-kaefer-sapphire-blue',
													)}
												>
													{item.isRead
														? t('notices.filter.read')
														: t('notices.filter.unread')}
												</span>
											</div>
										</AlertDescription>
									</div>
								</Alert>
							</button>
						)
					})}
				</div>
				<p className="mt-4 text-center text-muted-foreground text-xs">
					{items.length}{' '}
					{items.length === 1
						? t('notices.countSingular')
						: t('notices.countPlural')}
				</p>
			</>
		)
	}

	return (
		<div className="px-4 py-6 sm:px-6">
			<PageHeader
				title={t('notices.combinedTitle')}
				description={t('notices.combinedSubtitle')}
				onBack={() => navigate(ROUTES.DASHBOARD.HOME)}
			/>

			<Tabs defaultValue="unread">
				<div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<TabsList>
						<TabsTrigger value="unread">
							{t('notices.filter.unread')}
							{unreadCount > 0 && (
								<span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-kaefer-red px-1 text-[11px] text-white">
									{unreadCount}
								</span>
							)}
						</TabsTrigger>
						<TabsTrigger value="read">{t('notices.filter.read')}</TabsTrigger>
						<TabsTrigger value="all">{t('notices.filter.all')}</TabsTrigger>
					</TabsList>

					{unreadCount > 0 && (
						<Button
							variant="outline"
							size="sm"
							onClick={markAllAsRead}
							className="gap-2 hover:bg-transparent"
						>
							<CheckCheck className="size-4" />
							{t('notices.markAllRead')}
						</Button>
					)}
				</div>

				<TabsContent value="all">
					<FeedList items={feedItems} />
				</TabsContent>
				<TabsContent value="unread">
					<FeedList items={unreadItems} />
				</TabsContent>
				<TabsContent value="read">
					<FeedList items={readItems} />
				</TabsContent>
			</Tabs>
		</div>
	)
}
